import React, { useState } from 'react';
import useSEO from '../hooks/useSEO';
import { enviarContacto } from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    celular: '',
    email: '',
    comentarios: '',
    asunto: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  useSEO({
    title: 'Contacto - J&D Propiedad Raíz',
    description: 'Ponte en contacto con J&D Propiedad Raíz. Nuestro equipo de expertos inmobiliarios está listo para ayudarte a encontrar la propiedad perfecta o vender tu inmueble.',
    keywords: 'contacto, J&D Propiedad Raíz, inmobiliaria, asesoría, servicio al cliente, Medellín',
    ogTitle: 'Contacto - J&D Propiedad Raíz',
    ogDescription: 'Contáctanos para recibir asesoría personalizada en bienes raíces. Expertos en propiedades en Medellín y el Valle de Aburrá.',
    ogImage: 'https://jdpropiedadraiz.co/logo192.png',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto - J&D Propiedad Raíz",
      "description": "Página de contacto de J&D Propiedad Raíz - Inmobiliaria digital en Colombia",
      "mainEntity": {
        "@type": "Organization",
        "name": "J&D Propiedad Raíz",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "areaServed": "CO"
        }
      }
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await enviarContacto(formData);
      setSubmitStatus('success');
      setFormData({
        nombre: '',
        telefono: '',
        celular: '',
        email: '',
        comentarios: '',
        asunto: ''
      });
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black text-brand-white">
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-brand-gold">
            Contacto
          </h1>
          <p className="text-xl md:text-2xl text-brand-gray leading-relaxed">
            Estamos aquí para ayudarte en cada paso de tu experiencia inmobiliaria
          </p>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-2 h-20 bg-brand-gold opacity-30"></div>
        <div className="absolute bottom-10 right-10 w-2 h-20 bg-brand-gold opacity-30"></div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Formulario - Lado Izquierdo */}
            <div className="bg-brand-gold/5 p-8 rounded-2xl border border-brand-gold/20">
              <h2 className="text-3xl font-bold mb-8 text-brand-gold">
                Envíanos un mensaje
              </h2>
              
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500/40 text-green-400 p-4 rounded-lg mb-6">
                  ¡Mensaje enviado exitosamente! Te contactaremos pronto.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500/40 text-red-400 p-4 rounded-lg mb-6">
                  Error al enviar el mensaje. Por favor, inténtalo de nuevo.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre */}
                <div>
                  <label htmlFor="nombre" className="block text-brand-gold font-medium mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300"
                    placeholder="Ingresa tu nombre completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-brand-gold font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Teléfono y Celular en la misma fila */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="telefono" className="block text-brand-gold font-medium mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300"
                      placeholder="(4) 123 4567"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="celular" className="block text-brand-gold font-medium mb-2">
                      Celular
                    </label>
                    <input
                      type="tel"
                      id="celular"
                      name="celular"
                      value={formData.celular}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300"
                      placeholder="300 123 4567"
                    />
                  </div>
                </div>

                {/* Asunto */}
                <div>
                  <label htmlFor="asunto" className="block text-brand-gold font-medium mb-2">
                    Asunto *
                  </label>
                  <input
                    type="text"
                    id="asunto"
                    name="asunto"
                    value={formData.asunto}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300"
                    placeholder="¿En qué podemos ayudarte?"
                  />
                </div>

                {/* Comentarios */}
                <div>
                  <label htmlFor="comentarios" className="block text-brand-gold font-medium mb-2">
                    Comentarios *
                  </label>
                  <textarea
                    id="comentarios"
                    name="comentarios"
                    value={formData.comentarios}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-brand-black border-2 border-brand-gold/30 rounded-lg text-brand-white placeholder-brand-gray focus:border-brand-gold focus:outline-none transition-colors duration-300 resize-vertical"
                    placeholder="Cuéntanos más detalles sobre tu consulta..."
                  />
                </div>

                {/* Botón de envío */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-gold text-brand-black px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
              </form>
            </div>

            {/* Imagen - Lado Derecho */}
            <div className="lg:pl-8">
              <div className="relative">
                {/* Imagen principal */}
                <div className="aspect-w-4 aspect-h-5 rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Oficina moderna de J&D Propiedad Raíz"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Overlay decorativo */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-black/60 via-transparent to-transparent rounded-2xl"></div>
                
                {/* Contenido sobre la imagen */}
                <div className="absolute bottom-8 left-8 right-8 text-white bg-brand-black/60 p-4 rounded-lg">
                  <h3 className="text-2xl font-bold mb-4 text-brand-gold">
                    ¿Por qué elegirnos?
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-brand-gold rounded-full mr-3"></span>
                      <span>Atención personalizada 24/7</span>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-brand-gold rounded-full mr-3"></span>
                      <span>Proceso 100% digital</span>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-brand-gold rounded-full mr-3"></span>
                      <span>Respuesta inmediata</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Información de contacto adicional */}
              <div className="mt-8 bg-brand-gold/10 p-6 rounded-lg border border-brand-gold/20">
                <h4 className="text-xl font-semibold mb-4 text-brand-gold">
                  Otras formas de contacto
                </h4>
                <div className="space-y-3 text-brand-gray">
                  <div className="flex items-center">
                    <span className="text-brand-gold mr-3">📧</span>
                    <span>servicioalcliente@jdpropiedadraiz.co</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-brand-gold mr-3">📱</span>
                    <span>WhatsApp: +57 312 724 4825</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-brand-gold mr-3">🕒</span>
                    <span>Lun - Vie: 8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-brand-gold mr-3">📍</span>
                    <span>Medellín, Colombia</span>
                  </div>
                </div>
              </div>
              {/* Boton contacto adicional */}
              <div className="mt-8 bg-brand-gold/10 p-6 rounded-lg border border-brand-gold/20 text-center display: flex justify-center">
              <a
                  href="https://wa.me/573127244825?text=Hola,%20quiero%20contactar%20con%20ustedes."
                  className="w-full bg-brand-gold text-brand-black px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Contactar por WhatsApp
                </a>

              </div>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
